import { useCallback, useEffect, useRef, useState } from 'react';

import { flip, offset, shift, useFloating } from '@floating-ui/react';
import { useClickAway } from '@uidotdev/usehooks';
import { useHotkeys } from 'react-hotkeys-hook';

import { KEYCHAIN, useRegisterWithKeyMaster } from '../contexts/KeyMaster';

const useMessageReactionEmojibar = (
  workspaceId,
  channelId,
  messageId,
  { keychainId = KEYCHAIN.POPOVER__MESSAGE_REACTION_EMOJIBAR } = {},
) => {
  const [isOpen, setIsOpen] = useState(false);
  const justClosedRef = useRef(false);
  const offsetRef = useRef(20);
  const [frozenStyles, setFrozenStyles] = useState(null);
  const liveStylesRef = useRef(null);

  // Don't touch this it's delicate
  // We need the big emojipicker to be placed properly
  // in the virtual scroller, which is complicated
  const {
    refs,
    floatingStyles: liveFloatingStyles,
    update,
  } = useFloating({
    open: isOpen,
    placement: 'top',
    // Make sure the floating-ui doesn't go over the draggable zone, otherwise it can't be clicked on
    middleware: [
      offset(() => offsetRef.current),
      flip({
        padding: 40,
        fallbackStrategy: 'initialPlacement', // prefer 'top' if both overflow
        boundary: document.querySelector('[data-floating-boundary]'),
      }),
      shift({
        padding: 10,
        crossAxis: true,
        boundary: document.querySelector('[data-floating-boundary]'),
      }),
    ],
  });

  // Keep a ref to the latest live styles so we can capture them in rAF
  liveStylesRef.current = liveFloatingStyles;

  // Freeze position after initial calculation - ignore layout changes while open
  // This prevents the popover from jumping when reactions are added
  useEffect(() => {
    let rafId = null;

    if (isOpen && !frozenStyles) {
      // Wait for floating-ui to compute position, then freeze it
      rafId = requestAnimationFrame(() => {
        setFrozenStyles(liveStylesRef.current);
      });
    } else if (!isOpen && frozenStyles) {
      setFrozenStyles(null);
    }

    // Cancel pending rAF on unmount to avoid setState on unmounted component
    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [isOpen, frozenStyles]);

  const floatingStyles = frozenStyles || liveFloatingStyles;

  const setOffset = useCallback((value) => {
    offsetRef.current = value;
  }, []);

  const contentRef = useClickAway((event) => {
    // Don't close if meta key is held (for multi-select)
    if (event.metaKey) return;

    // Mark that we just closed, so toggle doesn't immediately reopen
    justClosedRef.current = true;
    setIsOpen(false);
    // Clear the flag after the click event finishes propagating
    requestAnimationFrame(() => {
      justClosedRef.current = false;
    });
  });

  // Toggle that skips reopen if clickaway just closed us (same click event)
  const toggle = useCallback(() => {
    if (justClosedRef.current) return;
    setIsOpen((prev) => !prev);
  }, []);

  // Register with KeyMaster when open
  const registeredKeychainId = isOpen ? keychainId : null;
  const { unlocked } = useRegisterWithKeyMaster(registeredKeychainId);

  // Close on Escape key (using KeyMaster pattern)
  useHotkeys(
    'escape',
    () => {
      if (unlocked()) {
        setIsOpen(false);
      }
    },
    { enabled: isOpen, enableOnFormTags: true, preventDefault: true },
    [unlocked],
  );

  const handleEmojiSelect = useCallback(
    async (emoji, options = {}) => {
      // dispatch event to live-journal controller to handle optimistic reaction creation and submission
      window.dispatchEvent(
        new CustomEvent('output:internal:live-journal:submit-reaction', {
          bubbles: true,
          detail: {
            reactionData: {
              channel_id: channelId,
              workspace_id: workspaceId,
              message_id: messageId,
              emoji_id: emoji.id,
            },
          },
        }),
      );

      if (!options.keepOpen) {
        setIsOpen(false);
      }
    },
    [channelId, messageId, workspaceId],
  );

  return { isOpen, setIsOpen, toggle, refs, floatingStyles, contentRef, handleEmojiSelect, update, setOffset };
};

export default useMessageReactionEmojibar;
